import React, { useEffect, useState } from 'react'
import { OverviewProjectsTableBodyLineProps } from './OverviewProjectsTableBodyLine.types'
import styles from './OverviewProjectsTableBodyLine.module.scss'
import cl from 'classnames'
import {
  Checkbox,
  ExpandButton,
  ImageSquare,
  TableBodyLine,
  TableCellProps,
  TableCellWithAdditionalCount,
  Text1,
  Text2
} from 'components/shared'
import { overviewProjectsTableWidths, expandedOverviewProjectsTableWidths } from '../OverviewProjectsTable.types'
import {
  STATISTICS_OVERVIEW_STORE,
  STATISTICS_OVERVIEW_TABLE_STORE,
  StatsOverviewTableEntity,
  StatsOverviewTableSortProps
} from 'store'
import { getOverviewProjectsTableFlexWidth } from '../OverviewProjectsTable.utils'
import { formatDuration, formatPrice, getMainImageSrcForObj } from 'utilities'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { ViewerEventEnums } from 'models'
import { isColEntityProduct } from './OverviewProjectsTableBodyLine.utils'
import { BestItem } from 'components/statistics/CirclePieSegment/BestItem'
import { StatsTableColType } from '../../../../../store/StatsBaseTableStore'
import { Grid } from "@material-ui/core"
import { parseMilliseconds } from "store/StatisticsPageStore/StatisticsPage.utils"
import moment from "moment"

const OverviewProjectsTableBodyLineComponent = (props: OverviewProjectsTableBodyLineProps) => {
  const {
    className,
    style,
    tableEntity,
    mainEntitySubLevel = 0,
    [STATISTICS_OVERVIEW_TABLE_STORE]: {
      isExpandedBlobsColumn,
      isExpandedProductPagesColumn,
      isExpandedPostersColumn,
      tableSettings,
      getSubTableEntity,
      onMainEntityCheckboxClick,
      isMainEntitySelected,
      getEntityGraphColor,
      onColumnEntityCheckboxClick,
      isCorrespondingEntitySelected,
      getSelectedCorrespondingEntitiesCount,
      isSomeCorrespondingEntitySelected,
      onColumnEntityAggregationCheckboxClick,
      isViewerEventShown,
      isColumnMarked,
      tableSortingCompareFn
    },
    [STATISTICS_OVERVIEW_STORE]: {
      isByExpensesTabSelected
    }
  } = props

  const [expandedColumn, setExpandedColumn] = useState<StatsTableColType>(null)

  // reset expand if table settings change
  useEffect(() => setExpandedColumn(null), [tableSettings])

  const overviewProjectsTableFlexWidth =
    getOverviewProjectsTableFlexWidth(tableSettings.draggableColumnsCount)

  const getDynamicTableCellProps = (
    tblEntity: StatsOverviewTableEntity
  ): TableCellProps[] => {
    const mainEntityHasChildren = Boolean(tblEntity.children?.length)

    const cells: TableCellProps[] = [
      {
        widthString: overviewProjectsTableFlexWidth,
        children: (
          <>
            {mainEntityHasChildren && (
              <ExpandButton
                isExpanded={expandedColumn === StatsTableColType.MAIN}
                onClick={() => setExpandedColumn(
                  s => s === StatsTableColType.MAIN
                    ? null
                    : StatsTableColType.MAIN
                )}
                style={{ marginLeft: mainEntitySubLevel * 25 }}
              />
            )}
            {(expandedColumn === StatsTableColType.MAIN ||
              Boolean(mainEntitySubLevel)) &&
              (
                <Checkbox
                  isChecked={isCorrespondingEntitySelected(
                    tableEntity.mainEntity.id,
                    tblEntity.mainEntity.id,
                    StatsTableColType.MAIN
                  )}
                  style={{
                    marginRight: 10,
                    marginLeft: (mainEntitySubLevel && !mainEntityHasChildren
                      ? mainEntitySubLevel * 25 + 2
                      : 0)
                  }}
                  onClick={() => onColumnEntityCheckboxClick(
                    tableEntity.mainEntity.id,
                    tblEntity.mainEntity.id,
                    StatsTableColType.MAIN
                  )}
                />
              )}

            {isColEntityProduct(tableSettings.mainEntityColLabel) && (
              <ImageSquare
                src={getMainImageSrcForObj(tblEntity.mainEntity)}
                width={30}
                showBorder
                style={{ marginRight: 12 }}
              />
            )}

            <Text1
              overflowEllipsis
              title={tblEntity.mainEntity.name}
            >
              {tblEntity.mainEntity.name}
            </Text1>
          </>
        ),
        style: {
          flex: 1,
          minWidth: overviewProjectsTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      },
      {
        widthString: overviewProjectsTableFlexWidth,
        children: tblEntity.primaryCorrespondingEntitiesCount > 1
          ? (
            <>
              <ExpandButton
                isExpanded={expandedColumn === StatsTableColType.PRIMARY}
                onClick={() => setExpandedColumn(
                  s => s === StatsTableColType.PRIMARY
                    ? null
                    : StatsTableColType.PRIMARY
                )}
              />
              {expandedColumn === StatsTableColType.PRIMARY && (
                <Checkbox
                  isChecked={isSomeCorrespondingEntitySelected(
                    tableEntity.mainEntity.id,
                    tblEntity.primaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.PRIMARY
                  )}
                  style={{ marginRight: 10 }}
                  onClick={() => onColumnEntityAggregationCheckboxClick(
                    tableEntity.mainEntity.id,
                    tblEntity.primaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.PRIMARY
                  )}
                />
              )}
              <Text2
                className={styles.textHighlighted}
              >
                {getSelectedCorrespondingEntitiesCount(
                  tableEntity.mainEntity.id,
                  tblEntity.primaryCorrespondingEntities.map(e => e.id),
                  StatsTableColType.PRIMARY
                )} selected
              </Text2>
              <Text2
                className={styles.textSecondary}
              >
                out of {tblEntity.primaryCorrespondingEntitiesCount}
              </Text2>
            </>
          )
          : (
            <>
              {isColEntityProduct(tableSettings.primaryCorrespondingEntityColLabel) && (
                <ImageSquare
                  src={getMainImageSrcForObj(tblEntity.primaryCorrespondingEntities?.length &&
                    tblEntity.primaryCorrespondingEntities[0])}
                  width={30}
                  showBorder
                  style={{ marginRight: 12 }}
                />
              )}
              <TableCellWithAdditionalCount
                text={tblEntity.primaryCorrespondingEntities?.length &&
                  tblEntity.primaryCorrespondingEntities[0]?.name}
                title={tblEntity.primaryCorrespondingEntities?.length &&
                  tblEntity.primaryCorrespondingEntities[0]?.name}
              />
            </>
          ),
        hoverComponent: tblEntity.primaryCorrespondingEntitiesCount > 1
          ? (
            <BestItem
              initialShowState
              positionFixed
              item={tblEntity.bestItemPrimary}
            />
          )
          : null,
        style: {
          flex: 1,
          minWidth: overviewProjectsTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      }
    ]

    if (tableSettings.draggableColumnsCount > 2) {
      cells.push({
        widthString: overviewProjectsTableFlexWidth,
        children: tblEntity.secondaryCorrespondingEntitiesCount > 1
          ? (
            <>
              <ExpandButton
                isExpanded={expandedColumn === StatsTableColType.SECONDARY}
                onClick={() => setExpandedColumn(
                  s => s === StatsTableColType.SECONDARY
                    ? null
                    : StatsTableColType.SECONDARY
                )}
              />
              {expandedColumn === StatsTableColType.SECONDARY && (
                <Checkbox
                  isChecked={isSomeCorrespondingEntitySelected(
                    tableEntity.mainEntity.id,
                    tblEntity.secondaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.SECONDARY
                  )}
                  style={{ marginRight: 10 }}
                  onClick={() => onColumnEntityAggregationCheckboxClick(
                    tableEntity.mainEntity.id,
                    tblEntity.secondaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.SECONDARY
                  )}
                />
              )}
              <Text2
                className={styles.textHighlighted}
              >
                {getSelectedCorrespondingEntitiesCount(
                  tableEntity.mainEntity.id,
                  tblEntity.secondaryCorrespondingEntities.map(e => e.id),
                  StatsTableColType.SECONDARY
                )} selected
              </Text2>
              <Text2
                className={styles.textSecondary}
              >
                out of {tblEntity.secondaryCorrespondingEntitiesCount}
              </Text2>
            </>
          )
          : (
            <>
              {isColEntityProduct(tableSettings.secondaryCorrespondingEntityColLabel) && (
                <ImageSquare
                  src={
                    getMainImageSrcForObj(tblEntity.secondaryCorrespondingEntities?.length &&
                      tblEntity.secondaryCorrespondingEntities[0])}
                  width={30}
                  showBorder
                  style={{ marginRight: 12 }}
                />
              )}
              <TableCellWithAdditionalCount
                text={tblEntity.secondaryCorrespondingEntities?.length &&
                  tblEntity.secondaryCorrespondingEntities[0]?.name}
                title={tblEntity.secondaryCorrespondingEntities?.length &&
                  tblEntity.secondaryCorrespondingEntities[0]?.name}
              />
            </>
          ),
        hoverComponent: tblEntity.secondaryCorrespondingEntitiesCount > 1
          ? (
            <BestItem
              initialShowState
              positionFixed
              item={tblEntity.bestItemSecondary}
            />
          )
          : null,
        style: {
          flex: 1,
          minWidth: overviewProjectsTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      })
    }

    return cells
  }

  const getStatsTableCellProps = (
    tableEntity: StatsOverviewTableEntity,
    isSubTableEntity?: boolean
  ): TableCellProps[] => {
    const widthCorrection = isByExpensesTabSelected ? 0 : 21
    const labelFunc = isByExpensesTabSelected ? formatPrice : (data) => data
    const cells = [
      {
        widthPx: isByExpensesTabSelected 
        ? (isExpandedBlobsColumn 
        ? expandedOverviewProjectsTableWidths.blobs 
        : overviewProjectsTableWidths.blobs - widthCorrection)
        : overviewProjectsTableWidths.blobs - widthCorrection,
        children: (
          <Grid container spacing={1} className={styles.gridContainer}   style={{
               opacity: isViewerEventShown(ViewerEventEnums.BLOB_TRIGGERED)
                 ? 1
                 : 0.2
             }}>
              
        {isByExpensesTabSelected && 
        <>
       {isExpandedBlobsColumn && <>
          <Grid>
            <div
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}
            >{labelFunc(tableEntity.stats.blobs)}
            </div>
          </Grid>
          <Grid>
          <div
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>
              {labelFunc(tableEntity.stats.blobAttached)}
          </div>
          </Grid>
          <Grid>
          <div
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>
              {labelFunc(tableEntity.stats.blobDetached)}
            </div>
          </Grid>
          </>}
        </>}
          <Grid>
          <div
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>
              {labelFunc(tableEntity.stats.blobsTotal)}
          </div>
          </Grid>
        </Grid>
        ),
        isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.BLOBS)
      },
      {
        widthPx: isByExpensesTabSelected 
        ? (isExpandedProductPagesColumn 
        ? expandedOverviewProjectsTableWidths.productPages 
        : overviewProjectsTableWidths.productPages - widthCorrection)
        : overviewProjectsTableWidths.productPages - widthCorrection,
        children: (

      <Grid container spacing={1} className={styles.gridContainer}   style={{
        opacity: isViewerEventShown(ViewerEventEnums.PRODUCT_PAGE_OPENED)
        ? 1
              : 0.2
          }}>
    {isByExpensesTabSelected && 
      <>
      {isExpandedProductPagesColumn && <>
       <Grid>
       <div 
        style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
        className={styles.paper}>
          {labelFunc(tableEntity.stats.product)}
          </div>
       </Grid>
       <Grid>
       <div 
        style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
        className={styles.paper}>
          {labelFunc(tableEntity.stats.productAdded)}
          </div>
       </Grid>
       </>}
      </>}
       <Grid>
       <div 
        style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
        className={styles.paper}>
          {labelFunc(tableEntity.stats.productTotal)}
          </div>
       </Grid>
     </Grid>
        ),
        isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.PRODUCT_PAGES)
      },
      {
        widthPx: isByExpensesTabSelected 
        ? (isExpandedPostersColumn 
        ? expandedOverviewProjectsTableWidths.posters 
        : overviewProjectsTableWidths.posters - widthCorrection)
        : overviewProjectsTableWidths.posters - widthCorrection,
        children: (
          <Grid container spacing={1} className={styles.gridContainer}   style={{
             opacity: isViewerEventShown(ViewerEventEnums.POSTER_CLICKED)
            ? 1
                  : 0.2
              }}>
        {isByExpensesTabSelected && 
          <>
        {isExpandedPostersColumn && <>
           <Grid>
           <div 
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>
              {labelFunc(tableEntity.stats.posters)}
              </div>
           </Grid>
           <Grid>
           <div 
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>
              {labelFunc(tableEntity.stats.posterAttached)}
              </div>
           </Grid>
            </>}
          </>}
           <Grid>
           <div 
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>
              {labelFunc(tableEntity.stats.posterTotal)}
              </div>
           </Grid>
         </Grid>
        ),
        isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.POSTERS)
      },
      {
        widthPx: overviewProjectsTableWidths.purchases - widthCorrection,
        children: (
          <Text1
            bold={Boolean(!isSubTableEntity && expandedColumn)}
            style={{
              opacity: isViewerEventShown(ViewerEventEnums.PURCHASE)
                ? 1
                : 0.2
            }}
          >
            {labelFunc(tableEntity.stats.purchases)}
          </Text1>
        ),
        isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.PURCHASES)
      },
      {
        widthPx: overviewProjectsTableWidths.vov - widthCorrection,
        children: (
          <Text1
            bold={Boolean(!isSubTableEntity && expandedColumn)}
            style={{
              opacity: isViewerEventShown(ViewerEventEnums.VOV_CLICKED)
                ? 1
                : 0.2
            }}
          >
            {labelFunc(tableEntity.stats.vov)}
          </Text1>
        ),
        isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.VOV)
      },
      {
        widthPx: overviewProjectsTableWidths.total - widthCorrection,
        children: (
          <Text1
            bold
            style={{
              color: tableEntity.stats.total === 0
                ? styles.error
                : undefined
            }}
          >
            {labelFunc(tableEntity.stats.total)}
          </Text1>
        ),
        isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.TOTAL)
      }
    ]

    if (!isByExpensesTabSelected) {

      cells.splice(3, 0,             
      {
         widthPx: overviewProjectsTableWidths.posters - widthCorrection,
         children: (
           <Text1
             bold={Boolean(!isSubTableEntity && expandedColumn)}
             style={{
               opacity: isViewerEventShown(ViewerEventEnums.POSTER_ATTACHED)
                 ? 1
                 : 0.2
             }}
           >
             {formatDuration(tableEntity.stats.totalPosterAttachedTime)}
           </Text1>
         ),
         isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.POSTERS)
       },
       {
        widthPx: overviewProjectsTableWidths.purchases - widthCorrection - 3,
        children: (
          <Text1
            style={{
              opacity: isViewerEventShown(ViewerEventEnums.ADD_TO_CART)
                ? 1
                : 0.2
            }}
          >
            {tableEntity.stats.productAdded}
          </Text1>
        ),
        isColumnMarked: isColumnMarked(StatsOverviewTableSortProps.ADDED_TO_CART)
      },
      )
    }

    return cells
  }

  return (
    <div
      className={cl(styles.container, {
        [styles.expandedZone]: expandedColumn && !mainEntitySubLevel,
        [styles.noBackgroundColor]: expandedColumn || mainEntitySubLevel
      }, className)}
      style={style}
    >
      <TableBodyLine
        cells={[
          {
            children: mainEntitySubLevel
              ? null
              : (
                <Checkbox
                  isChecked={isMainEntitySelected(tableEntity.mainEntity.id)}
                  fillColor={getEntityGraphColor(tableEntity.mainEntity.id)}
                  onClick={() => onMainEntityCheckboxClick(tableEntity.mainEntity.id)}
                />
              ),
            widthPx: overviewProjectsTableWidths.selector
          },
          ...getDynamicTableCellProps(tableEntity),
          ...getStatsTableCellProps(tableEntity)
        ]}
        containerProps={{
          style: {
            width: 'unset'
          }
        }} // For Proper table formatting
      />
      {expandedColumn === StatsTableColType.PRIMARY &&
        tableEntity.primaryCorrespondingEntities
          .map(entityObject => getSubTableEntity(
            tableEntity.mainEntity,
            entityObject,
            expandedColumn
          ))
          .sort(tableSortingCompareFn)
          .map(subTableEntity => {
            const entityObject = subTableEntity.primaryCorrespondingEntities[0]
            return (
              <TableBodyLine
                key={entityObject.id}
                cells={[
                  {
                    widthPx: overviewProjectsTableWidths.selector
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: overviewProjectsTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    children: (
                      <>
                        <Checkbox
                          isChecked={isCorrespondingEntitySelected(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                          style={{ marginLeft: 28 }}
                          onClick={() => onColumnEntityCheckboxClick(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                        />
                        {
                          isColEntityProduct(
                            tableSettings.primaryCorrespondingEntityColLabel
                          ) && (
                            <ImageSquare
                              src={getMainImageSrcForObj(entityObject)}
                              width={30}
                              showBorder
                              style={{ marginLeft: 12 }}
                            />
                          )
                        }
                        <Text1
                          overflowEllipsis
                          style={{ marginLeft: 10 }}
                          title={entityObject.name}
                        >
                          {entityObject.name}
                        </Text1>
                      </>
                    ),
                    style: {
                      flex: 1,
                      minWidth: overviewProjectsTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: overviewProjectsTableWidths.draggableColMinWidth,
                      maxWidth: 'unset',
                      display: tableSettings.draggableColumnsCount > 2 ? undefined : 'none'
                    }
                  },
                  ...getStatsTableCellProps(subTableEntity, true)
                ]}
              />
            )
          })}
      {expandedColumn === StatsTableColType.SECONDARY &&
        tableEntity.secondaryCorrespondingEntities
          .map(entityObject => getSubTableEntity(
            tableEntity.mainEntity,
            entityObject,
            expandedColumn
          ))
          .sort(tableSortingCompareFn)
          .map(subTableEntity => {
            const entityObject = subTableEntity.secondaryCorrespondingEntities[0]
            return (
              <TableBodyLine
                key={entityObject.id}
                cells={[
                  {
                    widthPx: overviewProjectsTableWidths.selector
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: overviewProjectsTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: overviewProjectsTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    children: (
                      <>
                        <Checkbox
                          isChecked={isCorrespondingEntitySelected(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                          style={{ marginLeft: 28 }}
                          onClick={() => onColumnEntityCheckboxClick(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                        />
                        {
                          isColEntityProduct(
                            tableSettings.secondaryCorrespondingEntityColLabel
                          ) && (
                            <ImageSquare
                              src={getMainImageSrcForObj(entityObject)}
                              width={30}
                              showBorder
                              style={{ marginLeft: 12 }}
                            />
                          )
                        }
                        <Text1
                          overflowEllipsis
                          style={{ marginLeft: 10 }}
                          title={entityObject.name}
                        >
                          {entityObject.name}
                        </Text1>
                      </>
                    ),
                    style: {
                      flex: 1,
                      minWidth: overviewProjectsTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  ...getStatsTableCellProps(subTableEntity, true)
                ]}
              />
            )
          })}

      {expandedColumn === StatsTableColType.MAIN &&
        tableEntity.children
          .sort(tableSortingCompareFn)
          .map((tEntity, index) => (
            <OverviewProjectsTableBodyLine
              key={index}
              tableEntity={tEntity}
              mainEntitySubLevel={mainEntitySubLevel + 1}
            />
          ))}
    </div>
  )
}

const OverviewProjectsTableBodyLine = inject(
  STATISTICS_OVERVIEW_TABLE_STORE, STATISTICS_OVERVIEW_STORE
)(observer(OverviewProjectsTableBodyLineComponent))
export { OverviewProjectsTableBodyLine }
